import React, { memo } from "react";
import Stop from "./Stop";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { Box, Typography, makeStyles, Button } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  countIndicator: {
    fontWeight: "500",
    lineHeight: "24px",
    color: "#4D4D50",
    "&.MuiButton-root": {
      padding: theme.spacing(0, 0.5, 0, 1),
      marginLeft: theme.spacing(-1)
    },
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center"
    }
  }
}));

const getStopInfo = (stopToFind, isSuggestion, solution) => {
  let isLocked = false;
  let finishedOrSkipped = false;
  let routeId = "";
  let routeStatus;
  if (isSuggestion || !solution || !stopToFind)
    return { finishedOrSkipped, isLocked, routeId, routeStatus };

  solution.routes.find(route => {
    return route.stops.find(task => {
      if (task.id === stopToFind.id) {
        finishedOrSkipped =
          task.status === "finished" || task.status === "skipped";
        isLocked = route.locked;
        routeId = route.id;
        routeStatus = route.status;
        return true;
      }
      return false;
    });
  });

  return { finishedOrSkipped, isLocked, routeId, routeStatus };
};

const StopRow = ({
  key, // Unique key within array of rows
  index, // Index of row within collection
  style, // Style object to be applied to row (to position it)
  ...props
}) => {
  const classes = useStyles();

  const {
    solution,
    filteredStops,
    filteredStopsLength,
    currentSelection,
    handleSelectStop,
    projectExpired,
    stopsLength,
    warnEdit,
    handleAdd,
    stopsInfoData,
    unscheduledExpanded,
    suggestions,
    editId,
    handleItemSelect,
    setUnscheduledExpanded,
    setHaveHovered,
    haveHovered,
    activeByListCommandCurrentIndex
  } = props;

  const unscheduledLength = unscheduledExpanded
    ? filteredStops.unscheduled.length
    : 0;
  const scheduledLength = filteredStops.scheduled.length;
  const hasUnscheduled = !!filteredStops.unscheduled.length;
  const hasScheduled = !!filteredStops.scheduled.length;

  // Adding headers as ordinary rows for virtualized list to keep general scroll container
  const maxHeaders =
    (solution ? 2 : 1) -
    Number(!hasUnscheduled) -
    (solution ? Number(!hasScheduled) : 0);

  const isSuggestion = index > filteredStopsLength + maxHeaders;

  const isHeader =
    // unscheduled header index
    index === 0 ||
    // suggestions header index
    index === filteredStopsLength + maxHeaders ||
    // scheduled header index
    (hasUnscheduled && hasScheduled && index === unscheduledLength + 1);

  // How we determine the index when we have unscheduled stops it goes after unscheduled stops and headers when we have not unscheduled and only scheduled stops subtract only scheduled stops and starts from them ~
  const scheduledStopsIndex = hasUnscheduled
    ? index - unscheduledLength - 2
    : index - 1;

  // get stop from filtered stops if current item is not header or suggestion
  const el =
    !isSuggestion &&
    !isHeader &&
    ((unscheduledExpanded && filteredStops.unscheduled[index - 1]) ||
      filteredStops.scheduled[scheduledStopsIndex]);

  const { finishedOrSkipped, isLocked, routeId, routeStatus } = getStopInfo(
    el,
    isSuggestion,
    solution
  );

  const isEditing = !isSuggestion && !isHeader && el.id === editId;

  const isScheduled =
    (!unscheduledExpanded || !stopsInfoData.unscheduled[index - 1]) &&
    !!stopsInfoData.scheduled[scheduledStopsIndex];

  const suggestion =
    isSuggestion && suggestions[index - filteredStopsLength - (maxHeaders + 1)];

  const selectedStopsInSearchResult = {
    unscheduled: filteredStops.unscheduled.filter(stop =>
      currentSelection.unscheduled.includes(stop.id)
    ).length,
    scheduled: filteredStops.scheduled.filter(stop =>
      currentSelection.scheduled.includes(stop.id)
    ).length
  };

  const offsets = {
    unscheduled: 1,
    scheduled: 1,
    suggestions: 1
  };

  if (hasUnscheduled) {
    offsets.scheduled += 1;
    offsets.suggestions += 1;
  }

  if (hasScheduled) {
    offsets.suggestions += 1;
  }

  return index === 0 && isHeader && hasUnscheduled ? (
    <Box key="header1" style={style}>
      <Box mt={3} mb={1} px={3}>
        <Typography
          component={solution ? Button : "p"}
          onClick={() => {
            solution && setUnscheduledExpanded(!unscheduledExpanded);
          }}
          className={classes.countIndicator}
          variant="body2"
        >
          {solution ? (
            <>
              <span>
                Unscheduled stops{" "}
                {`(${
                  selectedStopsInSearchResult.unscheduled
                    ? selectedStopsInSearchResult.unscheduled + "/"
                    : ""
                }${filteredStops.unscheduled.length})`}
              </span>
              {unscheduledExpanded ? (
                <ExpandLessIcon style={{ marginLeft: 4 }} />
              ) : (
                <ExpandMoreIcon style={{ marginLeft: 4 }} />
              )}
            </>
          ) : (
            `Added stops (${
              currentSelection.unscheduled.length
                ? selectedStopsInSearchResult.unscheduled + "/"
                : ""
            }${unscheduledLength})`
          )}
        </Typography>
      </Box>
    </Box>
  ) : suggestions.length && index === filteredStopsLength + maxHeaders ? (
    <Box key="header2" style={style}>
      <Box mt={3} mb={1} px={3}>
        <Typography className={classes.countIndicator} variant="body2">
          Add new stop
        </Typography>
      </Box>
    </Box>
  ) : suggestions.length && index > filteredStopsLength + maxHeaders ? (
    <Stop
      handleItemSelect={handleItemSelect}
      key={suggestion.el.id}
      style={style}
      el={suggestion.el}
      stopInfo={suggestion.info}
      isSuggestion
      suggestionIndex={index - (filteredStopsLength + maxHeaders) - 1}
      setHaveHovered={setHaveHovered}
      haveHovered={haveHovered}
      isActive={activeByListCommandCurrentIndex === index - offsets.suggestions}
    />
  ) : solution &&
    isHeader &&
    // Has unscheduled ok then scheduled header is next has not unscheduled then scheduled header is first
    (hasUnscheduled ? index === unscheduledLength + 1 : index === 0) ? (
    <Box key="header3" style={style}>
      <Box mt={3} mb={1} px={3}>
        <Typography className={classes.countIndicator} variant="body2">
          Scheduled stops (
          {selectedStopsInSearchResult.scheduled
            ? selectedStopsInSearchResult.scheduled + "/"
            : ""}
          {scheduledLength})
        </Typography>
      </Box>
    </Box>
  ) : (
    <Stop
      key={el.id}
      isLocked={isLocked}
      el={el}
      stopInfo={
        (unscheduledExpanded && stopsInfoData.unscheduled[index - 1]) ||
        stopsInfoData.scheduled[scheduledStopsIndex] ||
        []
      }
      isScheduled={isScheduled}
      projectExpired={projectExpired}
      style={style}
      handleSelect={handleSelectStop}
      isInCurrentSelection={currentSelection[
        isScheduled ? "scheduled" : "unscheduled"
      ].includes(el.id)}
      hasCurrentSelection={
        currentSelection.scheduled.length + currentSelection.unscheduled.length
      }
      editId={editId}
      isEditing={isEditing}
      routeStatus={routeStatus}
      routeId={routeId}
      editStop={warnEdit}
      addStop={handleAdd}
      stopsLength={stopsLength}
      notFirstSecondStops={index !== 0 && index !== 1}
      finishedOrSkipped={finishedOrSkipped}
      setHaveHovered={setHaveHovered}
      haveHovered={haveHovered}
      isActive={
        activeByListCommandCurrentIndex ===
        index -
          (index <= unscheduledLength ? offsets.unscheduled : offsets.scheduled)
      }
    />
  );
};

export default memo(StopRow);
